import i18n from 'i18n';
import { ColDef, ValueFormatterParams, ValueGetterParams } from '@ag-grid-enterprise/all-modules';
import { EWarrantInterceptStatus, EWarrantInterceptStatusValue, EWarrantStatus } from 'data/eWarrant/eWarrant.types';
import { adaptAuthStatus } from 'data/eWarrant/eWarrant.adapter';
import { dateFilterParams, dateTimeFormatter } from 'shared/constants';

/**
 * This value must be set in common-grid columnDefsVersion property. So, updating this value
 * forces common-grid to use the in code columnDefs instead of the saved grid setting.
 */
export const eWarrantColumnDefsVersion = '3';

const receivedValueFormatter = ({ value }: Partial<ValueFormatterParams>): string =>
  (value ? dateTimeFormatter(value) : '');

/**
 * The Status column shows the decision state the portal knows rather than the raw
 * AUTHSTATUS the row carries, which is why the values its filter offers are those labels
 * and not what travels to the backend — adaptToFilterModel on the list container turns
 * them back into AUTHSTATUS values.
 *
 * A set filter has to be told its values here: the grid reads its rows from the server, so
 * ag-grid never sees the whole column to collect them from.
 */
const statusValueGetter = ({ data }: Partial<ValueGetterParams>): string =>
  adaptAuthStatus(data?.status ?? '');

const statusFilterValues = [
  EWarrantStatus.Pending,
  EWarrantStatus.Approved,
  EWarrantStatus.Rejected,
];

/** Xcipio's provisioning-status dictionary, plus what the portal writes to start one. */
const interceptStatusFilterValues = [
  EWarrantInterceptStatusValue.NotCreated,
  EWarrantInterceptStatus.InProgress,
  EWarrantInterceptStatusValue.CreateUnderway,
  EWarrantInterceptStatusValue.CreateSuccess,
  EWarrantInterceptStatusValue.CreateError,
];

/**
 * Columns of the e-warrant list. The grid reads its rows from the server, so every field
 * here is a field of the list response (see EWarrantListItem) and every sort and filter
 * is sent to the backend, which maps these names onto the EWSAUTH columns Xcipio filters
 * and sorts on.
 *
 * That is also why a column with nothing behind it in EWSAUTH is not offered for sorting
 * or filtering: the requesting agency is parsed out of the raw HI-1 authorisation payload
 * of each row, so Xcipio has no column to order or filter it by.
 */
export const eWarrantColumnDefs: ColDef[] = [
  {
    headerName: i18n.t('eWarrant.grid.name'),
    field: 'warrant',
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    tooltipField: 'warrant',
    resizable: true,
    flex: 2,
    minWidth: 280,
  },
  {
    headerName: i18n.t('eWarrant.grid.sourceAgency'),
    field: 'requestingAgency',
    sortable: false,
    filter: false,
    tooltipField: 'requestingAgency',
    resizable: true,
    flex: 1,
    minWidth: 200,
  },
  {
    headerName: i18n.t('eWarrant.grid.receivedAt'),
    field: 'received',
    sortable: true,
    filter: 'agDateColumnFilter',
    filterParams: dateFilterParams,
    floatingFilter: true,
    resizable: true,
    flex: 1,
    minWidth: 180,
    valueFormatter: receivedValueFormatter,
    tooltipValueGetter: receivedValueFormatter as any,
  },
  {
    headerName: i18n.t('eWarrant.grid.status'),
    field: 'status',
    valueGetter: statusValueGetter,
    sortable: true,
    filter: 'agSetColumnFilter',
    filterParams: { values: statusFilterValues },
    floatingFilter: true,
    resizable: true,
    width: 140,
    cellRenderer: 'eWarrantStatusCellRenderer',
  },
  {
    headerName: i18n.t('eWarrant.grid.interceptStatus'),
    field: 'interceptStatus',
    sortable: true,
    filter: 'agSetColumnFilter',
    filterParams: { values: interceptStatusFilterValues },
    floatingFilter: true,
    resizable: true,
    width: 170,
    cellRenderer: 'eWarrantStatusCellRenderer',
  },
];
